/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/**
 * Documentation content for the in-app Help Center.
 *
 * Pure data so the article copy is easy to edit without touching render logic.
 * Each article is a list of typed blocks; see HELP screen (index.jsx) for the
 * renderer that maps these to design-system components.
 *
 * Block shapes:
 *   { t: 'h', text }                         — section heading
 *   { t: 'p', text }                         — paragraph
 *   { t: 'bullets', items: [text, ...] }     — bulleted list
 *   { t: 'steps', items: [[title, body]] }   — numbered steps
 *   { t: 'callout', tone, title, text }      — tip | info | warn
 *   { t: 'table', head: [...], rows: [[...]] }
 *   { t: 'cards', items: [[icon, title, text]] }
 *   { t: 'activities' }                      — the five activity-type legend
 */

const ARTICLES = [
  // ─────────────────────────────────────────────── Getting started
  {
    id: 'getting-started',
    label: 'Getting started',
    icon: 'Compass',
    title: 'Getting started',
    lead: 'As an admin or team leader you spend most of your time across three connected areas — Users, Teams and Reporting. This guide takes them in the order you would naturally set them up.',
    where: null,
    blocks: [
      { t: 'h', text: 'The three areas' },
      {
        t: 'cards',
        items: [
          ['Users', 'Users', 'Invite people, set account types and reporting managers, and control roles & permissions.'],
          ['Layers', 'Teams', 'Group members under leaders, track engagement, and tune per-team policies & visibility.'],
          ['BarChart3', 'Reporting', 'Read time worked, activity quality and audit results, then act on what the data shows.'],
        ],
      },
      { t: 'h', text: 'Who can do what' },
      { t: 'p', text: 'Every screen is gated by an account type. Higher levels inherit everything below them — this decides which items a person even sees in the left sidebar.' },
      {
        t: 'table',
        head: ['Account type', 'Level', 'Typical access'],
        rows: [
          ['Primary owner', '5', 'Everything, plus billing & workspace ownership'],
          ['Workspace owner', '4', 'Manage the workspace, all members & settings'],
          ['Workspace admin', '3', 'Members, teams, roles, reports & settings'],
          ['Team Leader', '2', 'Own team engagement, PTO & timesheets'],
          ['Team member', '1', 'Own activity, timesheets & PTO requests'],
        ],
      },
      { t: 'callout', tone: 'info', title: 'Roles go further than account types', text: 'Custom roles let you bundle exact permissions on top of the built-in tiers. See the Roles & permissions article.' },
      { t: 'h', text: 'A simple admin rhythm' },
      { t: 'p', text: 'When setting up a workspace — or onboarding a new team — this order keeps things tidy. The first three are one-time setup; the last two are ongoing.' },
      {
        t: 'steps',
        items: [
          ['Add users', 'Invite or import people; set account types and reporting managers.'],
          ['Set roles', 'Assign roles so each person sees only what they should.'],
          ['Build teams', 'Group members under leaders and tune team settings.'],
          ['Read reports', 'Watch Time Worked and engagement on a regular cadence.'],
          ['Act on flags', 'Review audits and alerts; follow up where it matters.'],
        ],
      },
      { t: 'h', text: 'Finding your way' },
      { t: 'p', text: 'The left sidebar adapts to your role. The key destinations referenced in this guide:' },
      {
        t: 'bullets',
        items: [
          'Team Engagement — teams and their engagement scores.',
          'Reports — Time Worked, audit results and timesheets.',
          'Manage Members — the full workspace user list.',
          'Settings & Permissions — screenshots, webcam and Team Management.',
          'Role Management — custom roles and permissions (owners).',
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────── Managing users
  {
    id: 'users',
    label: 'Managing users',
    icon: 'Users',
    title: 'Managing users',
    lead: 'The Workspace Members screen is where every person in your workspace lives. From here you invite new people, edit their details, and manage their access and tracking.',
    where: 'Sidebar → Manage Members',
    blocks: [
      { t: 'h', text: 'The Members table' },
      { t: 'p', text: 'Each row is one member. Use the controls above the table to focus the list:' },
      {
        t: 'bullets',
        items: [
          'Status filter — switch between Active, Inactive and All members.',
          'Search — find a member by name.',
          'Sort — click any column header to sort by it.',
        ],
      },
      { t: 'p', text: 'A member’s state is shown as a coloured status pill:' },
      {
        t: 'table',
        head: ['Status', 'Meaning'],
        rows: [
          ['Active', 'Member has accepted the invite and is tracking.'],
          ['Invited', 'Invitation sent — awaiting acceptance.'],
          ['Deactivated', 'Access revoked; can be reactivated later.'],
        ],
      },
      { t: 'h', text: 'Adding a member' },
      { t: 'p', text: 'Click + New Member and complete the form. An email invitation is sent automatically.' },
      {
        t: 'steps',
        items: [
          ['Email address', 'Where the invitation is sent.'],
          ['Account type', 'Team member, Team Leader, Workspace owner or Workspace admin.'],
          ['Department & designation', 'Pick from your workspace lists.'],
          ['Reporting manager', 'Sets the organisation-chart relationship.'],
          ['Projects (optional)', 'Assign the member to projects on creation.'],
        ],
      },
      { t: 'h', text: 'Bulk importing members' },
      { t: 'p', text: 'To add many people at once, click Import Users and upload a CSV.' },
      {
        t: 'steps',
        items: [
          ['Prepare your sheet', 'One row per person, with email and account-type columns.'],
          ['Upload the file', 'The importer previews your rows before anything is saved.'],
          ['Match the columns', 'Map each spreadsheet column to a Shoot field.'],
          ['Send invitations', 'Everyone is created together and emailed an invite.'],
        ],
      },
      { t: 'h', text: 'Managing an existing user' },
      { t: 'p', text: 'Open the row’s “···” (more) menu to reach every per-user action without leaving the table:' },
      {
        t: 'bullets',
        items: [
          'Edit User — update details, reporting manager and department.',
          'Resend Invitation — for members still in the Invited state.',
          'Setup Alerts — configure activity / violation alerts.',
          'Idle Time Tracking — enable or disable per member.',
          'Manual Tracker — allow manual time entries.',
          'Deactivate User — revoke access (reversible).',
        ],
      },
      { t: 'callout', tone: 'tip', title: 'Tip', text: 'Account type sets the broad tier. Use Role Management to fine-tune exactly which screens and actions a role gets — see Roles & permissions.' },
    ],
  },

  // ─────────────────────────────────────────────── Roles & permissions
  {
    id: 'roles',
    label: 'Roles & permissions',
    icon: 'KeyRound',
    title: 'Roles & permissions',
    lead: 'Roles let you go beyond the five built-in account types. Build custom roles that bundle exactly the permissions a job needs, then assign them to people.',
    where: 'Sidebar → Role Management',
    blocks: [
      { t: 'h', text: 'Creating and assigning a role' },
      {
        t: 'steps',
        items: [
          ['Create a role', 'Click Create Role, then give it a name, description and Level (1–5). A higher level means broader reach.'],
          ['Pick permissions', 'Toggle permission chips, grouped by resource — dashboard, report, team, member, settings and more.'],
          ['Assign to users', 'Use Assign Roles to map members to the roles you have built.'],
        ],
      },
      { t: 'h', text: 'System vs custom roles' },
      { t: 'p', text: 'Every role is marked System or Custom:' },
      {
        t: 'bullets',
        items: [
          'System roles map to the built-in account types and are view-only — you can inspect their permissions but not change them.',
          'Custom roles are fully editable: rename them, re-scope their permissions, or delete them.',
        ],
      },
      { t: 'callout', tone: 'info', title: 'Permissions are grouped by resource', text: 'Examples include dashboard:read, report:read, team:read, member:manage, settings:update, audit:webcam, pto:read:team and activity:read:all.' },
      { t: 'callout', tone: 'warn', title: 'Changing your own role', text: 'If you edit a role you currently hold, your access refreshes immediately so the change takes effect for you.' },
    ],
  },

  // ─────────────────────────────────────────────── Managing teams
  {
    id: 'teams',
    label: 'Managing teams',
    icon: 'Layers',
    title: 'Managing teams',
    lead: 'Teams group members under one or more leaders. The Team Engagement hub is your home base — KPIs at the top, quick filters, and a card for every team.',
    where: 'Sidebar → Team Engagement',
    blocks: [
      { t: 'h', text: 'The Team Engagement hub' },
      { t: 'p', text: 'The summary KPIs give you the shape of the workspace at a glance:' },
      {
        t: 'bullets',
        items: [
          'Total Teams — and how many members are tracked.',
          'Avg Engagement — the average score across all teams.',
          'Teams At Risk — teams below 40% engagement.',
        ],
      },
      { t: 'p', text: 'Use the engagement filters (All / High / Medium / At Risk) and the search box to narrow the cards below.' },
      { t: 'h', text: 'Creating a team' },
      { t: 'p', text: 'Workspace admins see a Create Team button. A team needs a name, at least one leader, and members.' },
      {
        t: 'steps',
        items: [
          ['Team name', 'A clear, recognisable name.'],
          ['Add leaders', 'One or more members who will lead the team.'],
          ['Add team members', 'The tracked contributors on the team.'],
        ],
      },
      { t: 'p', text: 'After creating, you can edit a team from its card at any time, drill in to see per-member activity, or delete it — deleting a team leaves its members in the workspace.' },
      { t: 'h', text: 'Roles inside a team' },
      {
        t: 'cards',
        items: [
          ['UserCog', 'Leaders', 'See their team’s engagement, timesheets and PTO. Usually a Team Leader account type.'],
          ['Users', 'Members', 'Tracked contributors whose activity rolls up into the team score.'],
        ],
      },
      { t: 'h', text: 'Team Management settings' },
      { t: 'p', text: 'Workspace-wide team policies live under Settings & Permissions → Team Management, organised into four tabs:' },
      {
        t: 'cards',
        items: [
          ['Table', 'Team Management', 'Audit and filter every member’s team assignments, screenshot rate, webcam audits and PTO policy — and fix anyone left Unassigned.'],
          ['Sliders', 'Global Team Settings', 'Set organisation-wide defaults: time-off policies, webcam-audit frequency, screenshot cadence and activity tracking.'],
          ['Eye', 'Team Visibility Controls', 'Decide who can view which teams and individual members.'],
          ['Mail', 'Productivity Email Settings', 'Manage the workspace’s automated productivity emails.'],
        ],
      },
      { t: 'h', text: 'How the engagement score works' },
      { t: 'callout', tone: 'info', title: 'The calculation', text: 'Engagement = Active time ÷ Total tracked time — computed per member over the chosen date range, then averaged across the team.' },
      { t: 'p', text: 'Use the bands to decide where to focus:' },
      {
        t: 'table',
        head: ['Band', 'Range', 'What to do'],
        rows: [
          ['High', '70% +', 'Healthy — keep doing what works.'],
          ['Medium', '40–69%', 'On track, but worth a closer look.'],
          ['At Risk', 'Below 40%', 'Flagged on the dashboard — act here first.'],
        ],
      },
      { t: 'callout', tone: 'warn', title: 'Violations', text: 'Team cards also flag webcam or geo-location violations independently of the engagement score.' },
    ],
  },

  // ─────────────────────────────────────────────── Reporting
  {
    id: 'reporting',
    label: 'Reporting',
    icon: 'BarChart3',
    title: 'Reporting',
    lead: 'Reports turn tracked activity into decisions. The Reports group in the sidebar collects every read-only view; availability depends on your permissions.',
    where: 'Sidebar → Reports',
    blocks: [
      { t: 'h', text: 'The reports at a glance' },
      {
        t: 'cards',
        items: [
          ['Clock', 'Time Worked', 'Hours, quality time and engagement per day, person or team.'],
          ['Camera', 'Webcam Audit Results', 'Review captured webcam-audit checks and their outcomes.'],
          ['ShieldAlert', 'Insider Threats', 'Surface risky behaviour patterns flagged for review.'],
          ['Table', 'Timesheets', 'Approve and review submitted time across the team.'],
          ['MapPin', 'Geolocation Results', 'Location-audit results and any geo-fence violations.'],
          ['CalendarOff', 'Team Members’ Time Off', 'Track PTO requests and balances for your reports.'],
        ],
      },
      { t: 'h', text: 'Inside the Time Worked report' },
      { t: 'p', text: 'Pick a date range, optionally filter to a team or member, then read four KPIs, a per-day breakdown chart, and an exportable table.' },
      {
        t: 'bullets',
        items: [
          'Date range — defaults to the last 7 days; pick any window.',
          'Filter — narrow to one team or a single member.',
          'KPIs — Total Hours, Manual Hours, Quality Hours and Engagement Score.',
          'Daily Activity — expand any day to see time by activity type.',
          'Export Table — download the breakdown as a CSV file.',
        ],
      },
      { t: 'h', text: 'The five activity types' },
      { t: 'p', text: 'Every report colours tracked time by these categories. Only the first three count toward the engagement (quality) score.' },
      { t: 'activities' },
      { t: 'h', text: 'Audit & compliance' },
      { t: 'p', text: 'Three reports help you verify presence and catch anomalies:' },
      {
        t: 'bullets',
        items: [
          'Webcam Audit Results — periodic checks confirm the right person is present; set the frequency in Settings → Webcam Audit.',
          'Geolocation Results — confirms members work from allowed locations; breaches appear as location violations.',
          'Insider Threats — highlights unusual or risky behaviour; Analytics → Unusual Activity goes deeper.',
        ],
      },
      { t: 'callout', tone: 'tip', title: 'Don’t just read — get alerted', text: 'Use Setup Alerts on a member (see Managing users) so webcam, location or activity issues notify you the moment they happen.' },
    ],
  },
];

export const ACTIVITY_TYPES = [
  { letter: 'D', color: '#213D69', name: 'Deep Focused', desc: 'Sustained, high-concentration work. The strongest signal of quality output.', scored: true },
  { letter: 'F', color: '#5A81BC', name: 'Focused', desc: 'Productive work with normal interaction. Counts toward quality time.', scored: true },
  { letter: 'A', color: '#B5BAC4', name: 'Active', desc: 'General active use of the computer. Lighter, but still engaged.', scored: true },
  { letter: 'W', color: '#F19235', name: 'Work Break', desc: 'Logged breaks. Tracked for context but excluded from the score.', scored: false },
  { letter: 'M', color: '#265EB8', name: 'Manual Hours', desc: 'Time added manually rather than auto-tracked. Reported separately.', scored: false },
];

export default ARTICLES;
